import { differenceInYears } from 'date-fns';
import { i18next } from '@frontend/i18n';
import { isPhoneNumber, sanitizePhoneNumber } from '@frontend/phone-numbers';
import * as PersonTypes from './types';

export const getNumberLabel = (number: string, person?: PersonTypes.Person) => {
  if (!person) {
    return '';
  }

  const sanitizedNumber = sanitizePhoneNumber(number.replace('+1', ''));

  if (sanitizedNumber === sanitizePhoneNumber(person.HomePhone)) {
    return 'Home';
  } else if (sanitizedNumber === sanitizePhoneNumber(person.MobilePhone)) {
    return 'Mobile';
  } else if (sanitizedNumber === sanitizePhoneNumber(person.WorkPhone)) {
    return 'Work';
  } else if (sanitizedNumber === sanitizePhoneNumber(person.OtherContactInfo)) {
    return 'Other';
  }

  return '';
};

export const getMobileNumber = (person?: PersonTypes.Person): string => {
  return person?.MobilePhone ?? '';
};

export const getAge = (data?: Partial<PersonTypes.Person>) => {
  let birthdate = data?.Birthdate;

  if (!birthdate) {
    return '';
  }

  /**
   * Remove the ISO portion of timestamp, including the Z
   * which forces UTC.
   */
  if (birthdate.includes('T')) {
    birthdate = birthdate.split('T')[0];
  }
  /**
   * Replace with time-zone neutral zeroed out time
   */
  birthdate += 'T00:00:00';

  const dateObj = new Date(birthdate);
  // adding this check to ignore DB default date (0001-01-01T00:00:00Z)
  if (dateObj.getFullYear() < 1900) return '';

  return differenceInYears(new Date(), dateObj);
};

/**
 * @deprecated - Please use `getFullName` from `@frontend/name` instead.
 */
export const getFullName = (person?: Partial<PersonTypes.Person> | null) => {
  const firstName = person?.FirstName ?? '';
  const lastName = person?.LastName ?? '';
  let preferredName = person?.PreferredName ?? '';

  if (preferredName?.includes(firstName) && preferredName?.includes(lastName)) {
    return preferredName;
  }
  if (preferredName.includes(lastName)) {
    preferredName = preferredName.replace(lastName, '');
  }
  if (Number(preferredName)) {
    preferredName = '';
  }

  return `${preferredName.trim() || firstName.trim()} ${lastName}`.trim();
};

export const getAddress = (person?: PersonTypes.Person): string[] => {
  return person?.Address && !isEmptyAddress(person?.Address)
    ? [
        person.Address.Street,
        `${person.Address.City !== '' ? `${person.Address.City}, ` : ' '}${person.Address.State} ${person.Address.Zip}`,
      ]
    : [];
};

const isEmptyAddress = (address: PersonTypes.Person['Address']): boolean => {
  if (!address) {
    return true;
  }

  const fields = Object.keys(address);
  for (let i = 0; i < fields.length; i++) {
    if (address[fields[i] as keyof PersonTypes.Person['Address']] !== '') {
      return false;
    }
  }

  return true;
};

export const getAllPhoneNumbers = (person?: PersonTypes.Person) => {
  const otherContactInfoIsPhoneNumber = isPhoneNumber(person?.OtherContactInfo);
  const phoneNumbers = [
    {
      label: i18next.t('Mobile Number', { ns: 'calls' }),
      number: person?.MobilePhone ?? '',
    },
    {
      label: i18next.t('Home Number', { ns: 'calls' }),
      number: person?.HomePhone ?? '',
    },
    {
      label: i18next.t('Work Number', { ns: 'calls' }),
      number: person?.WorkPhone ?? '',
    },
    {
      label: i18next.t('Other Number', { ns: 'calls' }),
      number: otherContactInfoIsPhoneNumber ? person?.OtherContactInfo ?? '' : '',
    },
  ].filter((phoneData) => !!phoneData.number);
  return phoneNumbers;
};
